import type { InfluencerRowView } from 'src/sections/influencer/utils/influencer-list-row-view';

import { useMemo, useState, useEffect } from 'react';

import Box from '@mui/material/Box';

import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { HubTable } from 'src/components/cmui/hub/hub-table';
import { SegmentTabs } from 'src/components/cmui/segment-tabs';
import { CreatorFinderRow, CREATOR_FINDER_GRID, CREATOR_FINDER_COLUMNS } from 'src/components/cmui/hub/creator-finder-row';

import { toCreatorRowView } from 'src/sections/creator-hub/utils/creator-row-stats';

import { CM } from '../constants/home-theme';
import { HomeSceneFrame } from './home-scene-frame';
import { CREATOR_DOCS } from '../constants/home-scene-samples';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

type CreatorSort = 'followers' | 'engagementRate';

/** 정렬 갈래 둘 — 이름은 크리에이터 허브의 정렬 이름 그대로다 */
const SORT_TABS: { key: CreatorSort; label: string }[] = [
  { key: 'followers', label: '팔로워순' },
  { key: 'engagementRate', label: '팔로워당 반응순' },
];

/** 장면이 들어온 뒤 정렬을 바꾸기까지(ms) — 「팔로워순」을 한 번 읽을 틈 */
const AUTO_SORT_DELAY_MS = 1600;

/** 이보다 팔로워가 적은 줄을 옅게 칠한다 — 정렬을 바꾸면 위로 오는 줄이다 */
const SMALL_ACCOUNT_MAX = 100000;

// ----------------------------------------------------------------------

/**
 * 장면 3 · 크리에이터 허브 — 같은 여섯 계정을 팔로워순과 팔로워당 반응순으로 견준다.
 *
 * 🔑 줄은 크리에이터 허브 목록의 줄 그대로다(`CreatorFinderRow` · `HubTable`) — 별(즐겨찾기)과 상세 열기는 없다.
 * 🔑 장면이 들어오면 한 번 「팔로워당 반응순」으로 저절로 바뀐다 — 그 전에 사람이 갈래를 누르면 그대로 둔다.
 */
export function HomeSceneCreators({ scene }: { scene: HomeScene }) {
  return (
    <HomeSceneFrame scene={scene} layout="stack">
      {(shown) => <CreatorTable shown={shown} />}
    </HomeSceneFrame>
  );
}

// ----------------------------------------------------------------------

function CreatorTable({ shown }: { shown: boolean }) {
  const [sort, setSort] = useState<CreatorSort>('followers');
  const [touched, setTouched] = useState(false);

  const rows = useMemo(() => {
    const views = CREATOR_DOCS.map((row) => ({ ...row, view: withLatinAverage(toCreatorRowView(row.doc), row.doc), followers: Number(row.doc.followerCount) }));
    const key = (row: (typeof views)[number]) =>
      sort === 'followers' ? row.followers : Number((row.doc.stats30d as { engagementRate: number }).engagementRate);
    return [...views].sort((a, b) => key(b) - key(a));
  }, [sort]);

  useEffect(() => {
    if (!shown || touched) return undefined;
    const timer = window.setTimeout(() => setSort('engagementRate'), AUTO_SORT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shown, touched]);

  const pick = (next: CreatorSort) => {
    setTouched(true);
    setSort(next);
  };

  return (
    <HubTable
      listBelowMd
      grid={CREATOR_FINDER_GRID}
      columns={CREATOR_FINDER_COLUMNS}
      tabs={
        /* 폰에서 정렬 알약을 엄지 크기(44px)로 키운다 — 공개 홈에서 처음 만지는 단추다 */
        <Box sx={{ '& button': { minHeight: { xs: 44, sm: 'auto' } } }}>
          <SegmentTabs value={sort} onChange={pick} tabs={SORT_TABS} />
        </Box>
      }
    >
      {rows.map((row, i) => (
        <Box
          key={row.view.key}
          sx={{
            bgcolor: row.followers < SMALL_ACCOUNT_MAX ? CM.alpha(0.06) : 'transparent',
            transition: 'background-color 0.4s',
            // 머리글자 얼굴을 또렷하게 — 서비스 줄은 옅은 회색 그대로다(A778)
            '& .MuiAvatar-root': { bgcolor: CM.primaryLighter, color: CM.primaryDark },
          }}
        >
          <CreatorFinderRow view={row.view} rank={i + 1} previewPosts={row.previews} saved={false} collecting={false} savingNow={false} saveDisabled />
        </Box>
      ))}
    </HubTable>
  );
}

/**
 * 「30일 평균 조회」를 팔로워 칸과 같은 `K·M` 으로 다시 적는다 — 서비스 줄 파생은 이 칸만 앱 로케일 축약(`16.77만`)이라
 * 한 줄에 `15.5K` 와 섞인다(A383 관찰 · 서비스 표기는 그대로 둔다).
 */
function withLatinAverage(view: InfluencerRowView, doc: Record<string, unknown>): InfluencerRowView {
  const avgView = Number((doc.stats30d as { avgView?: number } | undefined)?.avgView);
  return {
    ...view,
    statCells: view.statCells.map((cell) =>
      cell.label === '30일 평균 조회' && Number.isFinite(avgView) ? { ...cell, value: formatCompactNumber(avgView, { below: 'fNumber' }) } : cell
    ),
  };
}
